/**
 * KAN-204 — the ONE "Open Plan & billing" action, for every caller (the lock
 * dialog's button, the in-form notice's action) from any screen: the admin home,
 * a drawer, a pop-up, or a full page such as /log-job/:customerId.
 *
 *   1. close whatever is on top, newest first, through each overlay's own close
 *      path (`overlayDismissBus`) — a dirty one asks Stay / Leave, and Stay
 *      cancels the whole request;
 *   2. navigate: through the admin home's tab navigation when it is mounted (it
 *      keeps its own unsaved-settings confirms and the normal tab transition),
 *      otherwise straight to the Plan & billing URL with the router;
 *   3. idempotent — a second request while one is running is dropped.
 *
 * Client UX only: Plan & billing's data is admin-only on the server, and nothing
 * here is an authorization decision.
 */
import { dismissAllOverlaysWithReason } from '../ui/overlayDismissBus';
import { rememberView } from '../ui/subViewRoutes';

export const PLAN_BILLING_PATH = '/app/company/plan';
const COMPANY_PATH = /^\/app\/company(\/|$)/;

let planNavigator = null;
let inFlight = false;
let epoch = 0; // bumped at sign-out: a request started before it never navigates

/** The admin home registers its tab navigation while mounted: `() => Promise<boolean|void>` (false = declined). */
export function registerPlanNavigator(fn) {
  planNavigator = typeof fn === 'function' ? fn : null;
  return () => { if (planNavigator === fn) planNavigator = null; };
}

export function isOpenPlanBillingRunning() {
  return inFlight;
}

/**
 * Session-bound: `getSession()` ({ userId, tenantId }) is read at the start and again before
 * anything navigates; a different user / company (or a sign-out) means no navigation.
 * @param {{ navigate: (to: string, opts?: object) => void, pathname?: string, getSession?: () => ({ userId?: string, tenantId?: string }) }} deps
 * @returns {Promise<'navigated'|'cancelled'|'not-closed'|'busy'>} 'cancelled' = the user's own choice (Stay /
 *   declined); 'not-closed' = an overlay was busy or would not close (the caller tells the user).
 */
export async function openPlanBilling({ navigate, getSession, pathname = typeof window !== 'undefined' ? window.location.pathname : '' }) {
  if (inFlight) return 'busy';
  inFlight = true;
  const myEpoch = epoch;
  const start = getSession ? getSession() : null;
  const sameSession = () => {
    if (myEpoch !== epoch) return false;
    if (!getSession) return true;
    const now = getSession();
    return Boolean(start?.userId) && now?.userId === start.userId && now?.tenantId === start.tenantId;
  };
  try {
    const reason = await dismissAllOverlaysWithReason();
    if (!sameSession()) return 'cancelled';
    if (reason === 'stuck') return 'not-closed';
    if (reason !== 'done') return 'cancelled';
    rememberView('app/company', 'plan');
    // Already inside Company: the tab does not change, only its sub-view — the URL is the source of truth.
    if (!COMPANY_PATH.test(pathname) && planNavigator) {
      const result = await planNavigator();
      if (!sameSession()) return 'cancelled';
      return result === false ? 'cancelled' : 'navigated';
    }
    if (!sameSession()) return 'cancelled';
    navigate(PLAN_BILLING_PATH, { replace: true });
    return 'navigated';
  } finally {
    if (myEpoch === epoch) inFlight = false;
  }
}

/** Sign-out: a held request must never navigate the next session; a new request may start at once. */
export function resetOpenPlanBillingForSignOut() {
  epoch += 1;
  inFlight = false;
}

export function __resetOpenPlanBillingForTests() {
  planNavigator = null;
  inFlight = false;
  epoch = 0;
}

